import { useEffect, useState } from 'react';
import { Link, NavLink, Outlet, useLocation } from 'react-router-dom';
import { api, type AuroraStatus } from '../lib/api';
import { useAuth } from '../lib/auth';

/**
 * Persistent shell — header + nav + content slot.
 * Visual language: dense, editorial, dark surface, blue accent. surface.ai-
 * inspired (typography density, monospace metadata) but not a clone (different
 * palette, lower-case wordmark, warm-neutral base instead of blue-black).
 */
export function Layout(): JSX.Element {
  const auth = useAuth();
  const loc = useLocation();
  const isAuthRoute = loc.pathname === '/login';

  return (
    <div className="min-h-full flex flex-col">
      <header className="border-b border-bg-line bg-bg-surface/60 backdrop-blur sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 flex items-center gap-6">
          <Link to="/" className="wordmark hover:text-accent transition-colors">
            sjramblings <span className="wordmark-accent">/feed</span>
          </Link>
          {!isAuthRoute && auth.status === 'signed-in' && (
            <>
              <nav className="flex gap-1 text-sm">
                <NavTab to="/digests">Digests</NavTab>
                <NavTab to="/sources">Sources</NavTab>
                <NavTab to="/items">Items</NavTab>
                <NavTab to="/routing-rules">Routing rules</NavTab>
                <NavTab to="/digest/trigger">Trigger</NavTab>
              </nav>
              <div className="ml-auto flex items-center gap-3">
                <AuroraStatusIndicator />
                <span className="text-2xs font-mono text-ink-muted hidden sm:inline">
                  {auth.email}
                </span>
                <button
                  onClick={auth.signOut}
                  className="text-2xs font-mono text-ink-dim hover:text-ink transition-colors uppercase tracking-wider"
                >
                  Sign out
                </button>
              </div>
            </>
          )}
        </div>
      </header>
      <main className="flex-1">
        <Outlet />
      </main>
      <footer className="border-t border-bg-line py-3 text-center text-2xs font-mono text-ink-muted">
        feed-system-aws · ap-southeast-2 · single-user · cogito-ergo-jwt
      </footer>
    </div>
  );
}

function NavTab({ to, children }: { readonly to: string; readonly children: string }): JSX.Element {
  return (
    <NavLink
      to={to}
      className={({ isActive }) =>
        [
          'px-3 py-1.5 rounded font-mono text-2xs uppercase tracking-wider transition-colors',
          isActive
            ? 'bg-accent/10 text-accent border border-accent/30'
            : 'text-ink-dim hover:text-ink hover:bg-bg-raised border border-transparent',
        ].join(' ')
      }
    >
      {children}
    </NavLink>
  );
}

function AuroraStatusIndicator(): JSX.Element {
  const auth = useAuth();
  const [status, setStatus] = useState<AuroraStatus | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (auth.status !== 'signed-in' || auth.idToken === null) {
      setStatus(null);
      setError(null);
      return;
    }

    const idToken = auth.idToken;
    let cancelled = false;
    let ctrl = new AbortController();

    const fetchStatus = async (): Promise<void> => {
      ctrl.abort();
      ctrl = new AbortController();
      try {
        const s = await api.getAuroraStatus(idToken, ctrl.signal);
        if (!cancelled) {
          setStatus(s);
          setError(null);
        }
      } catch (e) {
        if ((e as { name?: string })?.name === 'AbortError') return;
        if (!cancelled) {
          setError(e instanceof Error ? e.message : String(e));
        }
      }
    };

    void fetchStatus();
    const interval = setInterval(() => { void fetchStatus(); }, 5 * 60_000);

    return () => {
      cancelled = true;
      ctrl.abort();
      clearInterval(interval);
    };
  }, [auth.status, auth.idToken]);

  const { dot, label } = describeAurora(status, error !== null);
  const title = status
    ? `Aurora ${status.state} (${status.cluster_status}${
        status.capacity_acu !== null ? `, ${status.capacity_acu} ACU` : ''
      }) - checked ${new Date(status.fetched_at).toLocaleTimeString()}`
    : error !== null
      ? `Aurora status check failed: ${error}`
      : 'Aurora status - loading...';

  return (
    <span
      className="text-2xs font-mono text-ink-muted hidden sm:inline-flex items-center gap-1.5"
      title={title}
    >
      <span className={`inline-block w-2 h-2 rounded-full ${dot}`} aria-hidden />
      <span>{label}</span>
    </span>
  );
}

function describeAurora(
  status: AuroraStatus | null,
  error: boolean,
): { readonly dot: string; readonly label: string } {
  if (error) return { dot: 'bg-red-500', label: 'aurora?' };
  if (status === null) return { dot: 'bg-gray-500 animate-pulse', label: 'aurora...' };
  switch (status.state) {
    case 'warm':
      return { dot: 'bg-green-500', label: 'aurora warm' };
    case 'paused':
      return { dot: 'bg-gray-500', label: 'aurora paused' };
    case 'starting':
      return { dot: 'bg-yellow-400 animate-pulse', label: 'aurora starting' };
    case 'stopped':
      return { dot: 'bg-red-500', label: 'aurora stopped' };
    case 'unknown':
      return { dot: 'bg-gray-400', label: 'aurora ?' };
    default:
      return { dot: 'bg-gray-400', label: 'aurora ?' };
  }
}
